/**
 * MattermostChannel.ts - Mattermost notification channel
 * 
 * CHANGE HISTORY:
 * - 251030_1215 [pending] danielbeer: Implemented actual Mattermost API calls
 * - 251030_0905 [pending] danielbeer: Created Mattermost channel for interactive confirmations
 */

import { Notification } from '../models/Notification';

export interface MattermostConfig {
  url: string;
  token: string;
  loggr?: any;
}

export interface MattermostAttachment {
  text?: string;
  actions?: Array<{
    name: string;
    integration: {
      url: string;
      context: Record<string, any>;
    };
  }>;
}

/**
 * Mattermost channel for sending interactive notifications
 */
export class MattermostChannel {
  private config: MattermostConfig;
  private loggr: any;
  
  constructor(config: MattermostConfig) {
    this.config = config;
    this.loggr = config.loggr;
  }
  
  /**
   * Send notification through Mattermost
   */
  async send(notification: Notification): Promise<any> {
    try {
      if (!this.config.url || !this.config.token) {
        throw new Error('Mattermost not configured');
      }
      
      this.loggr?.info('Sending Mattermost notification', {
        userId: notification.userId,
        title: notification.title
      });
      
      // Convert notification actions to Mattermost format
      const attachments: MattermostAttachment[] = [];
      
      if (notification.actions && notification.actions.length > 0) {
        attachments.push({
          text: notification.message,
          actions: notification.actions.map((action) => ({
            name: action.label,
            integration: {
              url: action.url || this.config.url,
              context: {
                action: action.action,
                notificationId: notification.id,
                ...action.data
              }
            }
          }))
        });
      }
      
      // Prepare message payload
      const payload: any = {
        channel_id: notification.userId, // Assumes userId is Mattermost channel ID
        message: `**${notification.title}**\n\n${notification.message}`
      };
      
      // Add attachments if actions exist
      if (attachments.length > 0) {
        payload.props = { attachments };
      }
      
      // Make actual API call to Mattermost
      const response = await fetch(`${this.config.url}/api/v4/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mattermost API error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      const messageId = result.id;
      
      this.loggr?.info('Mattermost notification sent', {
        userId: notification.userId,
        messageId,
        hasActions: attachments.length > 0
      });
      
      return {
        success: true,
        messageId,
        channel: 'mattermost',
        postId: result.id,
        channelId: result.channel_id
      };
      
    } catch (error) {
      this.loggr?.error('Failed to send Mattermost notification', {
        userId: notification.userId,
        error: error instanceof Error ? error.message : String(error)
      });
      
      throw error;
    }
  }
  
  /**
   * Check if channel is configured
   */
  isConfigured(): boolean {
    return !!(this.config.url && this.config.token);
  }
}
